import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../components/wrapper/API';
import { sortFlatRatesByName } from '../util/flatRates';

/**
 * Catalog of flat rates and deductions ("Pauschalen & Abzüge") for the admin page /flatrates and the pickers
 * of the template editor and the process page. `ensureLoaded()` fetches /FlatRate/GetAll at most once per component
 * instance (call it when the picker opens, not on mount); after a save the caller merges the response via
 * `upsert(flatRate)` instead of refetching. A failed load may be retried by the next ensureLoaded().
 */
export default function useFlatRates() {
  const [flatRates, setFlatRates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const ensureLoaded = useCallback(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    setLoading(true);
    setFailed(false);
    api.get('/FlatRate/GetAll')
      .then(res => {
        if (!mountedRef.current) return;
        setFlatRates(sortFlatRatesByName(Array.isArray(res.data) ? res.data : []));
        setLoaded(true);
      })
      .catch(() => {
        requestedRef.current = false;
        if (mountedRef.current) setFailed(true);
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, []);

  const upsert = useCallback((flatRate) => {
    if (!flatRate || flatRate.id == null) return;
    setFlatRates(prev => sortFlatRatesByName([...prev.filter(f => f.id !== flatRate.id), flatRate]));
  }, []);

  const remove = useCallback((id) => {
    setFlatRates(prev => prev.filter(f => f.id !== id));
  }, []);

  return { flatRates, loaded, loading, failed, ensureLoaded, upsert, remove };
}
